import React, { Fragment, ReactNode, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { labelDesign } from '@teetsh/app/src/components/Form/Label';
import tw from 'twin.macro';
import PortalWrapper from '@teetsh/app/src/components/PortalWrapper';
import { menuItemsStyle } from '@teetsh/app/src/components/Menu/MenuItems';
import { menuItemStyle } from '@teetsh/app/src/components/Menu/MenuItem';
import { CheckIcon } from '@heroicons/react/20/solid';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import styled from '@emotion/styled';
import clsx from 'clsx';
import { bgColors } from '@teetsh/app/src/components/colors';
import { ButtonContent } from '@teetsh/app/src/components/Buttons/ButtonContent';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { BaseButtonProps } from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import { buttonRootStyle } from '@teetsh/app/src/components/Buttons/Button';
import { Color } from '@teetsh/app/src/types/colors';

export type Value = string | number | undefined;

export type Option<T> =
  | {
      value: T;
      label: string | ReactNode;
      sublabel?: string | ReactNode;
      color?: Color;
      group?: never;
    }
  | {
      group: true;
      label: string;
      sublabel?: string | ReactNode;
      color?: Color;
      value?: never;
    };

export interface Props<T> {
  open: boolean;
  options: Option<T>[];
  large?: boolean;
  id?: string;
  fill?: boolean;
  label?: string;
  portal?: boolean;
  value?: T;
  onChange: (v: T) => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
  children?: React.ReactNode;
  withoutCheck?: boolean;
  withoutChevron?: boolean;
  openOnMount?: boolean;
  className?: string;
  onClickOutside?: (event: any) => void;
  size?: 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'auto' | 'full';
}

const StyledListboxButton: any = styled(
  ({
    buttonRef,
    className,
    children,
    open,
    color,
    withoutChevron,
    ...rest
  }) => (
    <Listbox.Button
      ref={buttonRef}
      className={clsx('tsh-button', className)}
      aria-label={rest['aria-label']}
      data-testid={rest['data-testid']}
      css={[tw`appearance-none`, color && bgColors(color)]}
    >
      <ButtonContent
        {...rest}
        rightIcon={withoutChevron ? null : ChevronDownIcon}
      >
        {children}
      </ButtonContent>
    </Listbox.Button>
  ),
)((p: BaseButtonProps) => [buttonRootStyle(p)]);

const sizeToWidth = {
  xs: tw`w-32`,
  sm: tw`w-52`,
  md: tw`w-64`,
  lg: tw`w-72`,
  xl: tw`w-80`,
  auto: tw`w-auto`,
  full: tw`w-full`,
};

export function SelectBody<T extends Value>(props: Props<T>) {
  const {
    label,
    options,
    value,
    withoutCheck,
    withoutChevron,
    className,
    portal,
    open,
    openOnMount,
    onClickOutside,
  } = props;
  const matchingOption = options
    .filter(opt => 'value' in opt)
    .find(opt => opt.value === value);

  const { refs, floatingStyles } = useFloating({
    middleware: [
      offset(2),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const handleClickOutside = event => {
    if (refs.floating && refs.floating.current) {
      if (!refs.floating.current.contains(event.target)) {
        if (onClickOutside) {
          return onClickOutside(event);
        }
      }
    }
  };
  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return;
  });

  useEffect(() => {
    if (refs.reference?.current && openOnMount) {
      (refs.reference.current as any)?.click();
    }
  }, []);

  return (
    <>
      {label && (
        <Listbox.Label css={[labelDesign, tw`mb-1`]}>{label}</Listbox.Label>
      )}
      <div tw="relative" css={[props.fill && tw`w-full`]}>
        <div tw="flex">
          <StyledListboxButton
            {...props}
            buttonRef={refs.setReference}
            fill
            aria-label={props['aria-label']}
            className={className}
            withoutChevron={withoutChevron}
            alignText="left"
            hasChildren
            color={matchingOption?.color}
            open={open}
          >
            <span ref={props.buttonRef} tw="flex-grow">
              {matchingOption?.label || '‎'}
            </span>
          </StyledListboxButton>
          {props.children}
        </div>
        <PortalWrapper portal={portal}>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 50,
            }}
          >
            {/*@ts-ignore*/}
            <Transition
              show={open}
              as={Fragment as any}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                static
                css={[menuItemsStyle, sizeToWidth[props.size || 'full']]}
                tw="z-30 mt-1 max-h-60 py-1 text-base overflow-auto focus:outline-none sm:text-sm"
              >
                {options.map(option =>
                  option.group ? (
                    <div
                      key={option.label}
                      tw="relative py-2 pl-3 pr-3 text-xs text-neutral-quiet"
                    >
                      {option.label}
                    </div>
                  ) : (
                    <Listbox.Option key={option.value} value={option.value}>
                      {({ selected, active }) => (
                        <div
                          css={[
                            menuItemStyle({ active }),
                            tw`cursor-pointer select-none relative py-2 pl-3 pr-3 focus:outline-none`,
                            selected && !withoutCheck && tw`pr-11`,
                            selected && tw`bg-brand-quiet`,
                          ]}
                        >
                          <span
                            css={[
                              tw`font-normal block truncate`,
                              selected && tw`font-semibold text-brand-intense`,
                              props.large && tw`text-base`,
                            ]}
                          >
                            {option.label}
                          </span>
                          {option.sublabel && (
                            <span
                              css={[
                                tw`font-normal text-xs block pr-8 text-muted`,
                                selected &&
                                  tw`font-semibold text-brand-intense`,
                              ]}
                            >
                              {option.sublabel}
                            </span>
                          )}

                          {selected && !withoutCheck ? (
                            <span tw="absolute top-0 bottom-0 right-0 flex items-center pr-4">
                              <CheckIcon tw="h-5 w-5 text-brand-intense" />
                            </span>
                          ) : null}
                        </div>
                      )}
                    </Listbox.Option>
                  ),
                )}
              </Listbox.Options>
            </Transition>
          </div>
        </PortalWrapper>
      </div>
    </>
  );
}
